import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdCheckCircle, MdError } from 'react-icons/md';
import { useAppDispatch } from '../redux/hooks';
import { clearCart } from '../redux/slices/cartSlice';
import { request } from '../api/request';
import ENDPOINTS from '../api/endpoints';

/**
 * QuanticPaymentCallback Component
 * 
 * Monitors URL parameters for Quantic payment callback and updates payment status.
 * Production-ready with error handling, accessibility, and auto-redirect.
 * 
 * Expected URL format: /any-page?orderId=xxx&restaurantId=xxx
 */
export const QuanticPaymentCallback = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const processedRef = useRef(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const orderId = searchParams.get('orderId');
  const restaurantId = searchParams.get('restaurantId');

  const removePaymentParams = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('orderId');
    params.delete('restaurantId');
    setSearchParams(params, { replace: true });
  };

  const handleClose = () => {
    setStatus('idle');
    removePaymentParams();
    navigate('/menu', { replace: true });
  };

  // Payment status update effect with memory leak prevention
  useEffect(() => {
    // Handle missing parameters
    if (!orderId || !restaurantId) {
      if (orderId || restaurantId) {
        console.error('[Payment Callback] Missing required parameters:', { orderId, restaurantId });
        setStatus('error');
      }
      return;
    }

    // Prevent duplicate API calls
    if (processedRef.current) return;
    processedRef.current = true;

    let mounted = true;
    setStatus('loading');

    const updatePaymentStatus = async () => {
      try {
        const response: any = await request({
          url: `${ENDPOINTS.API_V2_BASE_URL}${ENDPOINTS.PAYMENT_STATUS_UPDATE_API_PATH}`,
          method: 'POST',
          headers: {
            'restaurant-id': restaurantId,
          },
          data: {
            posOrderId: orderId,
            paymentStatus: 'paid',
          },
        });

        // Prevent state update if component unmounted
        if (!mounted) return;

        if (response?.data?.success) {
          dispatch(clearCart());
          removePaymentParams();
          setStatus('success');
        } else {
          console.error('[Payment Callback] API returned unsuccessful response:', response);
          setStatus('error');
        }
      } catch (error) {
        console.error('[Payment Callback] Payment status update failed:', error);
        if (mounted) {
          setStatus('error');
        }
      }
    };

    updatePaymentStatus();

    // Cleanup function to prevent memory leaks
    return () => {
      mounted = false;
    };
  }, [orderId, restaurantId, dispatch]);

  // Removed auto-redirect - user must click button to navigate

  // Loading State
  if (status === 'loading') {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-loading-title"
        className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md mx-4"
        >
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <h3 id="payment-loading-title" className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              Processing Payment
            </h3>
            <p className="text-sm sm:text-base text-gray-600">
              Please wait while we confirm your payment...
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success State
  if (status === 'success') {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-success-title"
        className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md mx-4"
        >
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <MdCheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
            </div>
            <h3 id="payment-success-title" className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              Payment Successful!
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Your payment has been processed successfully. Thank you for your order!
            </p>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Continue to menu"
              className="w-full min-h-[48px] bg-red-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Continue to Menu
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error State
  if (status === 'error') {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-error-title"
        className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md mx-4"
        >
          <div className="p-6 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <MdError className="h-8 w-8 text-red-600" aria-hidden="true" />
            </div>
            <h3 id="payment-error-title" className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              Payment Update Failed
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              We couldn't update your payment status. Please contact support if you need assistance.
            </p>
            <button
              type="button"
              onClick={handleClose}
              aria-label="Go to menu"
              className="w-full min-h-[48px] bg-red-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Go to Menu
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
};
